'use client';

import * as React from 'react';
import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { AutocompleteOptionList } from './autocomplete-option-list';
import { Input } from './input';
import { Label } from './label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet';
import type { AutocompleteViewProps } from './use-autocomplete';

/**
 * Mobile rendering for `Autocomplete`: a single trigger button plus a bottom
 * sheet with the search input and option list. Extracted from `autocomplete.tsx`
 * (conformance, code-size guard).
 */
export function AutocompleteMobile({ props, state }: AutocompleteViewProps) {
  const {
    multiple,
    options,
    className,
    label,
    required,
    disabled,
    inputClassName,
    error,
    placeholder,
    hint,
    trailingAction,
    loading,
    loadingMessage,
    header,
    maxSelections,
  } = props;
  const ariaLabel = props['aria-label'];
  const describedBy =
    [props['aria-describedby'], error ? `${state.baseId}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  // Also drives the destructive border, so a `FormControl` error (whose text
  // `FormMessage` renders) marks the field without a second message.
  const invalid =
    !!error ||
    props['aria-invalid'] === true ||
    props['aria-invalid'] === 'true';
  const {
    selectedValues,
    atLimit,
    displayValue,
    labelId,
    inputId,
    isOpen,
    setIsOpen,
    listboxId,
    handleClear,
    handleSelect,
    inputRef,
    inputValue,
    handleInputChange,
    listRef,
    statusId,
    filteredOptions,
    settleTypedText,
    discardTypedText,
  } = state;

  // Radix reports every dismissal as onOpenChange(false). Remember whether it
  // was Escape, so Escape discards typed text as on desktop instead of
  // committing it like an outside tap does.
  const dismissedByEscape = React.useRef(false);

  // Multi-select surfaces selections as a rich removable chip row BELOW the
  // trigger (owned by the primitive — wrappers must not add their own strip),
  // mirroring the desktop in-field chips. The trigger itself then just shows
  // the placeholder. Single-select keeps its collapsed label in the trigger.
  const mobileTriggerText = multiple ? '' : displayValue;

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label id={labelId} htmlFor={inputId}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Trigger for mobile. The clear button sits beside the trigger inside
          one bordered field, never inside it (a button in a button is invalid
          HTML and axe nested-interactive) and never over it (an overlapping
          target fails WCAG 2.5.8 target size). */}
      <div
        className={cn(
          'border-input bg-background flex h-11 w-full items-center rounded-md border',
          'focus-within:ring-ring focus-within:ring-1',
          disabled && 'cursor-not-allowed opacity-50',
          invalid && 'border-destructive',
          inputClassName,
        )}
      >
        <button
          type="button"
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          onClick={() => setIsOpen(true)}
          disabled={disabled}
          className="flex h-full min-w-0 flex-1 items-center justify-between gap-2 rounded-md py-2 pr-2 pl-3 text-left focus-visible:outline-hidden disabled:cursor-not-allowed"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
        >
          <span
            className={cn(
              'truncate',
              !mobileTriggerText && 'text-muted-foreground',
            )}
          >
            {mobileTriggerText || placeholder}
          </span>
          <ChevronDownIcon
            className="text-muted-foreground h-4 w-4 shrink-0"
            aria-hidden="true"
          />
        </button>
        {selectedValues.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="hover:bg-muted focus-visible:ring-ring mr-1 flex size-9 shrink-0 items-center justify-center rounded-sm focus-visible:ring-1 focus-visible:outline-hidden"
            aria-label="Clear selection"
          >
            <Cross2Icon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {hint && !error && (
        <p className="text-muted-foreground text-sm">{hint}</p>
      )}
      {error && (
        <p id={`${state.baseId}-error`} className="text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Selected chips (multi) — primitive-owned; wrappers add no strip. */}
      {multiple && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedValues.map((v) => {
            const opt = options.find((o) => o.value === v);
            const chipLabel = opt?.label ?? v;
            return (
              <span
                key={v}
                className="bg-muted text-foreground inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-1 pl-1.5 text-sm"
              >
                {opt?.icon && <span className="flex-shrink-0">{opt.icon}</span>}
                <span className="max-w-[10rem] truncate">{chipLabel}</span>
                <button
                  type="button"
                  onClick={() => handleSelect(v)}
                  aria-label={`Remove ${chipLabel}`}
                  className="hover:bg-foreground/10 text-muted-foreground hover:text-foreground rounded-sm p-0.5 transition-colors"
                  disabled={disabled}
                >
                  <Cross2Icon className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Selection cap counter */}
      {multiple && maxSelections ? (
        <p
          className={cn(
            'text-xs',
            atLimit ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {selectedValues.length} / {maxSelections}
        </p>
      ) : null}

      {/* Mobile Sheet */}
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          // Dismissing the sheet without tapping an option settles typed text,
          // or the trigger would show it as if it were the selection.
          if (!open) {
            if (dismissedByEscape.current) discardTypedText();
            else settleTypedText();
          }
          dismissedByEscape.current = false;
          setIsOpen(open);
        }}
      >
        <SheetContent
          side="bottom"
          className="max-h-[85vh] px-0"
          onEscapeKeyDown={() => {
            dismissedByEscape.current = true;
          }}
        >
          <SheetHeader className="px-4 pb-2">
            <SheetTitle>{label || placeholder}</SheetTitle>
          </SheetHeader>

          {header ? (
            <div className="text-muted-foreground px-4 pb-2 text-sm">
              {header}
            </div>
          ) : null}

          {/* Search input in sheet */}
          <div className="border-b px-4 pb-3">
            <div className="relative">
              <Input
                ref={inputRef}
                type="text"
                role="searchbox"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={(e) => {
                  // Same as desktop selectOnFocus: typing replaces the label.
                  if (props.selectOnFocus && !multiple)
                    e.currentTarget.select();
                }}
                placeholder={placeholder}
                className={cn(
                  'h-11 text-base', // text-base prevents iOS zoom
                  trailingAction ? 'pr-16' : 'pr-10',
                )}
                autoFocus
              />
              <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-1">
                {trailingAction}
                {loading && (
                  <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                )}
              </div>
            </div>
          </div>

          {/* Options list */}
          <div
            ref={listRef}
            role="listbox"
            aria-label={label || ariaLabel}
            className="max-h-[60vh] overflow-y-auto"
          >
            <AutocompleteOptionList props={props} state={state} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Screen reader announcements */}
      <div
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {loading
          ? loadingMessage
          : `${filteredOptions.length} results available`}
      </div>
    </div>
  );
}
